/** Tool definitions: Brokerage sync and file import. */

import { SYNC_HISTORY_YEARS_DEFAULT, SYNC_HISTORY_YEARS_MAX, SYNC_HISTORY_YEARS_MIN } from '../syncHistory.js';
import type { ToolDefinition } from './types.js';

export const syncImportTools = {
  snaptrade_fetch_activities: {
    name: 'snaptrade_fetch_activities',
    access: 'read',
    description:
      'DEBUG: Live-fetch raw SnapTrade activities from the broker for one connected account in a date range. Returns the broker payload untouched. Use together with get_transactions_diagnostic to find activities the broker reports but Luminaria did not save.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID containing the account.',
        },
        accountId: {
          type: 'string',
          description: 'Luminaria account ID. Must be SnapTrade-connected.',
        },
        startDate: {
          type: 'string',
          description: 'Start date in YYYY-MM-DD format.',
        },
        endDate: {
          type: 'string',
          description: 'End date in YYYY-MM-DD format.',
        },
        symbol: {
          type: 'string',
          description: 'Optional symbol substring filter applied to underlying/ticker (case-insensitive).',
        },
      },
      required: ['portfolioId', 'accountId'],
    },
  },
  get_sync_status: {
    name: 'get_sync_status',
    access: 'read',
    description:
      'Get sync health for all connected accounts: lastSyncAt, lastSyncStatus, lastSyncError, brokerDataAsOf (when SnapTrade last successfully pulled transactions FROM the brokerage — null means the initial pull has not completed), transaction counts with date range (earliestTransaction shows how far back stored history reaches), and stale-holding counts. Also returns syncHistoryYears, the user\'s full-history sync window. Use to diagnose missing transactions or stale holdings before triggering a sync.',
    inputSchema: {
      type: 'object',
      properties: {},
    },
  },
  sync_accounts: {
    name: 'sync_accounts',
    access: 'write',
    description:
      'Trigger a SnapTrade sync. mode "full" (default) replaces holdings and adds new transactions for one account (accountId) or all connected accounts (no accountId). mode "force_transactions_refresh" asks SnapTrade to refresh from the brokerage first, then refetches the user\'s full history window (syncHistoryYears years, default 1) of transactions for one account (accountId required) — use when transactions are missing, brokerDataAsOf is stale/null, or after raising syncHistoryYears to backfill older history. Re-fetching is deduplicated, so it is safe to repeat; note SnapTrade rate-limits brokerage refreshes to ~1/day per connection.',
    inputSchema: {
      type: 'object',
      properties: {
        accountId: {
          type: 'string',
          description: 'Luminaria account ID. Required for force_transactions_refresh; optional for full (omit to sync all connected accounts).',
        },
        mode: {
          type: 'string',
          enum: ['full', 'force_transactions_refresh'],
          description: 'Sync mode. Defaults to "full".',
        },
      },
    },
  },
  update_sync_settings: {
    name: 'update_sync_settings',
    access: 'write',
    description:
      `Set how many years of activity history a full-history SnapTrade sync pulls (syncHistoryYears, ${SYNC_HISTORY_YEARS_MIN}-${SYNC_HISTORY_YEARS_MAX}, default ${SYNC_HISTORY_YEARS_DEFAULT}). It applies to a new connection's first sync and to sync_accounts with mode force_transactions_refresh; ordinary syncs stay incremental, and the weekly automatic resync always uses 1 year. Raising it does not fetch anything by itself: run sync_accounts force_transactions_refresh on each account to backfill. SnapTrade returns only what the brokerage still retains (about 2 years for Schwab), so get_sync_status earliestTransaction shows what actually arrived.`,
    inputSchema: {
      type: 'object',
      properties: {
        syncHistoryYears: {
          type: 'integer',
          minimum: SYNC_HISTORY_YEARS_MIN,
          maximum: SYNC_HISTORY_YEARS_MAX,
          description: `Years of history (${SYNC_HISTORY_YEARS_MIN}-${SYNC_HISTORY_YEARS_MAX}).`,
        },
      },
      required: ['syncHistoryYears'],
    },
  },
  import_transactions: {
    name: 'import_transactions',
    access: 'write',
    description:
      'Import broker transactions from CSV file content (Vanguard, Schwab, and other supported formats — broker is auto-detected). Parses the content, then persists transactions to the given account with signature-based duplicate detection, so re-importing or overlapping with SnapTrade sync is safe. Holdings rows in the file are ignored (transactions only). Use dryRun:true first to preview what would be imported without writing.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID containing the account.',
        },
        accountId: {
          type: 'string',
          description: 'Luminaria account ID to import transactions into.',
        },
        content: {
          type: 'string',
          description: 'Raw CSV file content.',
        },
        fileName: {
          type: 'string',
          description: 'Optional original file name (helps broker/file-type detection).',
        },
        dryRun: {
          type: 'boolean',
          description: 'If true, parse and return the preview (broker, counts, warnings, sample) without importing. Defaults to false.',
        },
      },
      required: ['portfolioId', 'accountId', 'content'],
    },
  },
} satisfies Record<string, ToolDefinition>;
